/**
 * `<xac-legend>` — internal shared legend (registered as `xacLegend`).
 *
 * Renders the legend exactly as the design reference: a wrapper div with
 * class `xac-legend xac-legend-{layout}` containing one real
 * `<button type="button">` per item (keyboard accessible, `aria-pressed`
 * reflecting visibility). When `toggleable` is `false` (pie), items render as
 * static `<span>` elements with class `xac-legend-static` instead — no
 * `aria-pressed`, no click handling.
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `items` | `XaLegendItem[]` | — | `{ label, color, value, visible }` from the core layout |
 * | `layout` | `'row' \| 'column'` | `'row'` | from `legendLayoutFor()` |
 * | `show-values` | `boolean` | `false` | render `span.xac-legend-value` |
 * | `toggleable` | `boolean` | `true` | `false` = static (pie) |
 * | `on-toggle` | `&` callback | — | invoked as `onToggle({ label })`; deferred ~250ms so a double-click can cancel it |
 * | `on-isolate` | `&` callback | — | invoked as `onIsolate({ label })` on double-click |
 */
import type { XaLegendItem, XaLegendLayout } from '@xait/charts-core';

/**
 * Single-click delay (ms) used to disambiguate a toggle from a double-click
 * isolate: a `dblclick` fires two `click`s, so the toggle is deferred via
 * `$timeout` and a double-click cancels it before it runs.
 */
const CLICK_DELAY_MS = 250;

class XacLegendController implements ng.IController {
  static $inject = ['$timeout'];

  items?: XaLegendItem[];
  layout?: XaLegendLayout;
  showValues?: boolean;
  toggleable?: boolean;
  onToggle!: (locals: { label: string }) => void;
  onIsolate!: (locals: { label: string }) => void;

  private clickTimer: ng.IPromise<void> | null = null;

  constructor(private readonly $timeout: ng.ITimeoutService) {}

  /**
   * Single click: defer the toggle via `$timeout` so a following double-click
   * can cancel it (a `dblclick` always emits two `click`s first).
   */
  handleClick(label: string): void {
    if (this.clickTimer) this.$timeout.cancel(this.clickTimer);
    this.clickTimer = this.$timeout(() => {
      this.clickTimer = null;
      this.onToggle({ label });
    }, CLICK_DELAY_MS);
  }

  /** Double click: cancel the pending toggle and isolate this item. */
  handleDblClick(label: string): void {
    if (this.clickTimer) {
      this.$timeout.cancel(this.clickTimer);
      this.clickTimer = null;
    }
    this.onIsolate({ label });
  }

  $onDestroy(): void {
    if (this.clickTimer) {
      this.$timeout.cancel(this.clickTimer);
      this.clickTimer = null;
    }
  }
}

export const xacLegendComponent: ng.IComponentOptions = {
  controller: XacLegendController,
  bindings: {
    items: '<',
    layout: '<',
    showValues: '<',
    toggleable: '<',
    onToggle: '&',
    onIsolate: '&',
  },
  template: `<div ng-if="$ctrl.toggleable !== false" class="xac-legend" ng-class="'xac-legend-' + ($ctrl.layout || 'row')">
    <button type="button" ng-repeat="it in $ctrl.items track by $index"
        class="xac-legend-item" ng-class="{ 'xac-legend-off': !it.visible }"
        aria-pressed="{{it.visible}}"
        ng-click="$ctrl.handleClick(it.label)"
        ng-dblclick="$ctrl.handleDblClick(it.label)">
      <span class="xac-swatch" ng-style="{ background: it.color }"></span>
      <span class="xac-legend-label">{{it.label}}</span>
      <span class="xac-legend-value" ng-if="$ctrl.showValues">{{it.value}}</span>
    </button>
  </div>
  <div ng-if="$ctrl.toggleable === false" class="xac-legend" ng-class="'xac-legend-' + ($ctrl.layout || 'row')">
    <span ng-repeat="it in $ctrl.items track by $index" class="xac-legend-item xac-legend-static">
      <span class="xac-swatch" ng-style="{ background: it.color }"></span>
      <span class="xac-legend-label">{{it.label}}</span>
      <span class="xac-legend-value" ng-if="$ctrl.showValues">{{it.value}}</span>
    </span>
  </div>`,
};
